import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/nextjs';
import BuildUrl from 'build-url';
import { logAmplitude } from '../../utils/amplitude';
import searchClient from '../../utils/algolia';
import { isBefore, parseISO, subMonths } from 'date-fns';

import {
  createRecentlyViewed,
  getRecentlyViewed,
  getProduct as fetchProduct,
  getHasProductRecommendation,
  getProductStructuredData
} from '../api';

import { toggleCartModalVisibility } from '../../store/modules/interfaces/actions';

import { Page, Content } from '../../theme/page';
import { PDPMeta } from '../../utils/meta';
import { trackPageType, trackProductView } from '../../utils/tracking';
import getCookie from '../../utils/getCookie';
import hasError from '../../utils/hasError';
import SCOPE_TYPES from '../../utils/sentryScopeTypes';

import ProductDetailsSkeleton from '../../organs/pdp-details-skeleton';
import ProductDetails from '../../organs/pdp-details';
import MobilePaidProductDetails from '../../organs/pdp-mobile-paid-details';
import ErrorPage from '../_error';
import ProductAddedConfirmation from '../../tissues/product-confirm-cart';
import MobileBottomSheet from '../../tissues/mobile-bottomsheet';

// Context
import { useProduct } from '../../utils/context/product-provider';
import { showPromotion } from '../../store/modules/promotions/actions';
import PdpPromoBanner from '../../organisms/pdp-promo-banner';

const ProductDetailPage = (props) => {
  const { isMobileDevice, growthbookFeatureFlagValues, isMWebPaidExperience, isPdpPromoBannerVisible } = props;
  const index = searchClient.initIndex(process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX);
  const router = useRouter();

  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [hasRecommendations, setHasRecommendations] = useState(false);

  const {
    state: { productVariantAdded, productAddedModalActive, ctxIsMWebPaidPdpExperience },
    setProductAddedModalActive,
    setCtxIsMWebPaidPdpExperience
  } = useProduct();

  useEffect(() => {
    setCtxIsMWebPaidPdpExperience(true);
    if (props?.product?.master?.id) {
      getHasProductRecommendation({ id: props.product.master.id })
        .then((res) => {
          setHasRecommendations(res);
        })
        .catch(() => {
          setHasRecommendations(false);
        });
    }
  }, [props.product]);

  useEffect(() => {
    global.window.scrollTo(0, 0);
    setCtxIsMWebPaidPdpExperience(isMWebPaidExperience);
  }, []);

  const setProductAsRecentlyViewed = async (variant_id) => {
    const session_id = getCookie('maisonette_session_token');

    // TODO: Assess this stopgap / guard clause against no session_id
    // An ideal flow here may be to create a session_id if one does not exist first
    if (!session_id) {
      setRecentlyViewed([]);
      return;
    }

    await createRecentlyViewed({
      body: {
        recently_viewed: {
          session_id,
          variant_id
        }
      }
    });
  };

  const getRecentlyViewedProducts = () => {
    const session_id = getCookie('maisonette_session_token');

    // TODO: Assess this stopgap / guard clause against no session_id
    if (!session_id) {
      setRecentlyViewed([]);
      return;
    }

    // Assuming a session_id, get the recently viewed products
    getRecentlyViewed({ session_id }).then(async (res) => {
      if (hasError(res)) {
        setRecentlyViewed([]);
        return;
      }

      try {
        const ids = res
          // converting to string as getObjects method accept string array
          .map((x) => x.variant_id.toString())
          // remove current product from ids
          .filter((id) => Number(id) !== props.product.master.id)
          .slice(0, 20);

        if (ids) {
          /**
           * getObjects lets you retrieve multiple objects from a specified index
           * For more details, please check: https://www.algolia.com/doc/api-reference/api-methods/get-objects
           */
          index.getObjects(ids).then(({ results }) => {
            // filter to remove the null record
            setRecentlyViewed(results.filter((result) => result));
          });
        }
      } catch (error) {
        setRecentlyViewed([]);
      }
    });
  };

  useEffect(() => {
    if (props.product && !hasError(props.product)) {
      // trackPageType is here to ensure that the GA page view event
      // happens before the product view event
      // TODO: Address these calls when updating the google analytics system
      trackPageType('Product Description Pages', router.asPath);
      trackProductView({ product: props.product });
      setProductAsRecentlyViewed(props.product.master.id);
      getRecentlyViewedProducts();
    }
  }, [props.product]);


  const productType = (() => {
    const { classifications } = props.product;

    if (!Array.isArray(classifications)) {
      return 'Unknown'; // Handle cases where classifications is not an array
    }
    
    const [producttype] = classifications.filter((c) => {
      const prettyName = c?.taxon?.pretty_name;
      return typeof prettyName === 'string' && prettyName.split('->')?.[0]?.trim() === 'Product Type';
    });
  
    return producttype?.taxon?.name;
  })();

  if (router?.isFallback) {
    return (
      <Page id="maincontent">
        <Content>
          <ProductDetailsSkeleton />
        </Content>
      </Page>
    );
  }

  if (!props.product || hasError(props.product)) {
    return <ErrorPage statusCode={404} />;
  }

  const hasImages = props.product?.master && props.product?.master?.images;

  const getProductVariantAdded = (addedVariantId) => {
    const variantDetails = props.product?.variants.filter((v) => v.id === addedVariantId)[0];
    return variantDetails;
  };

  const getQuantityInBag = (addedVariantId) => {
    const itemInCart = props.lineItems?.filter((l) => l.variant_id === addedVariantId)[0];
    return itemInCart?.quantity;
  };

  const closeAddedtoBagModal = () => setProductAddedModalActive(false);

  const onCloseModal = () => {
    closeAddedtoBagModal();
    logAmplitude('Add to Cart Confirmation', {
      product: props.product,
      isViewed: false,
      isContinue: true
    });
  };

  return (
    <div key={props.product.id}>
      <PDPMeta product={props.product} productStructuredData={props.productStructuredData} />
      <Page id="maincontent">
        {isMWebPaidExperience && isPdpPromoBannerVisible ? <PdpPromoBanner growthbookFeatureFlagValues={growthbookFeatureFlagValues}/> : null}
        <Content>
          {isMWebPaidExperience && ctxIsMWebPaidPdpExperience ? (
            <MobilePaidProductDetails
              product={props.product}
              hasImages={hasImages}
              productType={productType}
              hasRecommendations={hasRecommendations}
              recentlyViewed={recentlyViewed}
              meta={props.meta}
            />
          ) : (
            <ProductDetails
              product={props.product}
              hasImages={hasImages}
              productType={productType}
              hasRecommendations={hasRecommendations}
              recentlyViewed={recentlyViewed}
              meta={props.meta}
            />
          )}
        </Content>
      </Page>

      {isMobileDevice && global?.document && (
        <MobileBottomSheet isOpen={productAddedModalActive} onClose={onCloseModal}>
          <ProductAddedConfirmation
            variant={getProductVariantAdded(productVariantAdded)}
            brand={props.product?.brand}
            quantity={getQuantityInBag(productVariantAdded)}
            closeModal={closeAddedtoBagModal}
            toggleCart={props.toggleCart}
            product={props.product}
          />
        </MobileBottomSheet>
      )}
    </div>
  );
};

export async function getServerSideProps(context) {
  const { query, req } = context;
  const { slug, ...params } = query;

  // if there is a 'quantity' param,
  // redirect to the PDP URL without it
  // to avoid indexing the query param in Google
  if (params.quantity) {
    delete params.quantity;

    const url =
      params && Object.keys(params).length ? BuildUrl(`/product/${slug}`, { queryParams: params }) : `/product/${slug}`;

    return {
      redirect: {
        destination: url,
        permanent: true
      }
    };
  }

  const getProduct = (id) =>
    fetchProduct({
      uri: `/api/products/${id}`,
      scopes: [SCOPE_TYPES.SERVICES.SOLIDUS],
      id
    });

  const _getProductStructuredData = (slug) =>
    getProductStructuredData({
      uri: `/api/pdp-structured-data/${slug}`,
      scopes: [SCOPE_TYPES.SERVICES.REST_API],
      slug
    });

  // if there was an error fetching product
  // - return null for product,
  // - revalidation will try to fetch again on next attempt
  const product = await getProduct(slug).catch((error) => {
    Sentry.withScope((scope) => {
      scope.setContext('slug', slug);
      scope.setFingerprint('/pages/product/[slug]::getServerSideProps');
      Sentry.captureException(error);
    });

    return null;
  });

  if (product && hasError(product)) return { notFound: true };

  if (product?.last_seen_purchasable_at) {
    const lastSeenPurchasableAt = product?.last_seen_purchasable_at;
    const currentDate = new Date();
    const date12MonthsAgo = subMonths(currentDate, 12);
    const isOlderThan12Months = isBefore(parseISO(lastSeenPurchasableAt), date12MonthsAgo);
    if (isOlderThan12Months) {
      const { breadcrumb_taxons } = product;
      const lowestTaxonsIndex = breadcrumb_taxons.length - 1;
      const breadcrumbTaxonsUrl = breadcrumb_taxons[lowestTaxonsIndex].url.replace('/category', '');
      return {
        redirect: {
          destination: `${process.env.NEXT_PUBLIC_CLIENT_HOST}/shop/${breadcrumbTaxonsUrl}`,
          permanent: true
        }
      };
    }
  }

  // Structured Data
  let productStructuredData = null;
  try {
    const productStructuredDataResponse = await _getProductStructuredData(slug);
    productStructuredData = productStructuredDataResponse?.data ?? null;
  } catch (error) {
    productStructuredData = null;
  }

  return {
    props: {
      product,
      productStructuredData,
      meta: {
        referer: req.headers?.referer || 'direct'
      }
    }
  };
}

const mapStateToProps = (state) => ({
  lineItems: state.cart.line_items,
});

const mapDispatchToProps = (dispatch) => ({
  toggleCart: (isActive) => dispatch(toggleCartModalVisibility(isActive)),
  togglePromotion: (isVisible) => dispatch(showPromotion(isVisible))
});

ProductDetailPage.defaultProps = {
  product: null,
  productStructuredData: null,
  toggleCart: () => {},
  togglePromotion: () => {},
  lineItems: null,
  isMobileDevice: false,
  meta: {},
  growthbookFeatureFlagValues: {},
  isMWebPaidExperience: false,
  isPdpPromoBannerVisible: false
};

ProductDetailPage.propTypes = {
  product: PropTypes.object,
  productStructuredData: PropTypes.object,
  toggleCart: PropTypes.func,
  togglePromotion: PropTypes.func,
  lineItems: PropTypes.array,
  isMobileDevice: PropTypes.bool,
  meta: PropTypes.object,
  growthbookFeatureFlagValues: PropTypes.object,
  isMWebPaidExperience: PropTypes.bool,
  isPdpPromoBannerVisible: PropTypes.bool
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductDetailPage);
